import debounce from "lodash.debounce";
import { useState, useEffect } from "react";
import { ROW_PER_PAGE_DEFAULT, MIN_SEARCH_LENGTH, DEBOUNCE_DELAY } from "Theme/config";
import { calculateNewPage } from "Tools/calculateNewPage";
import useTableHandlers from "./useTableHandlers";

function useTableUserSettings<TOrdering>(dataKey: string, defaultOrderBy: TOrdering) {
  const [selectedIds, setSelectedIds] = useState<string[] | null>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROW_PER_PAGE_DEFAULT);
  const [orderBy, setOrderBy] = useState<TOrdering>(defaultOrderBy);
  const [personFilter, setPersonFilter] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage(0);
  }, [dataKey]);

  const { handleSelect, handleUnselect } = useTableHandlers({
    onUpdate: setSelectedIds,
    selectedIds
  });

  const onSearchChange = (value: string) => {
    const filter = value.trim();

    if (filter && filter.length < MIN_SEARCH_LENGTH) {
      return;
    }

    setPersonFilter(filter);
  };

  const debouncedOnSearchChange = debounce(
    onSearchChange,
    DEBOUNCE_DELAY
  );

  const handleSearch = (search: string) => {
    setSelectedIds([]);
    setCurrentPage(0);
    debouncedOnSearchChange(search);
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(calculateNewPage(currentPage, rowsPerPage, newRowsPerPage));
  };

  const onChangeOrderBy = (newOrderBy: TOrdering) => {
    setOrderBy(newOrderBy);
  };

  const onChangePage = (newPage: number) => {
    setCurrentPage(newPage);
  };

  return {
    selectedIds,
    currentPage,
    rowsPerPage,
    orderBy,
    personFilter,
    handleSearch,
    onChangeRowsPerPage,
    onChangeOrderBy,
    onChangePage,
    handleSelect,
    handleUnselect,
    setSelectedIds
  };
}

export default useTableUserSettings;